import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import IcdAutocomplete from "../chiefComplaints/IcdAutocomplete";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import { Stepper } from "../controls";
import { useCardDraft } from "../common/useCardDraft";
import HospitalisationService from "./HospitalisationService";
import ProcedureCatalogAutocomplete from "./ProcedureCatalogAutocomplete";
import { HOSP_V2_REFRESH } from "./events";
import { CalendarIcon, ChevronUpIcon } from "../labInvestigations/labIcons";

/**
 * ONE hospitalisation sheet — add or update — owning all of its own field state.
 *
 * This used to be the body of HospitalisationEditor, with every field hoisted
 * into that component and gated on formOpen + editing. It was split out so the
 * rail can hold a STACK of sheets: each card needs its own cause / status /
 * dates / procedures, and sibling cards must not see each other's values. The
 * rail (HospitalisationEditor) now only decides which cards exist and in what
 * order; everything below the card border lives here.
 *
 * "Card · Add hospitalisation (in-screen)" (3098:2681, 340 wide). The sheet
 * serves BOTH flows: ADD (toolbar / empty state → HOSP_V2_ADD) and UPDATE (row
 * cause or status chip → HOSP_V2_EDIT, carrying `{ hospitalisation }`). The old
 * centered HospitalisationModal is gone — same move labInvestigations made when
 * its rail absorbed InvestigationModal.
 *
 * BOTH modes render the Y/M/D steppers above each date box, and BOTH route
 * their date boxes through editHospDate / editDischargeDate, so the counters
 * and the dates stay in sync in both directions everywhere:
 *   · hospitalisation steppers = time SINCE admission (today − y/m/d)
 *   · discharge steppers       = stay DURATION      (admission + y/m/d)
 * which is exactly how the board's sample resolves (1y6m ago → 05 Jan 2025,
 * +3m stay → 05 Apr 2025, "Auto-calc · Duration: 3 months"). An update sheet
 * therefore opens with its counters already describing the saved dates (see
 * the seeding below) rather than at 00/00/00, which used to make them lie and
 * made the first nudge jump the date to today.
 *
 * Status* is the Discharged | Hospitalised segment (board order, 3073:7084) —
 * required, no silent default in Add. Hospitalised disables + clears the
 * discharge block (backend rule) and the duration reads as ongoing.
 *
 * There is NO delete affordance: the editor's "Delete this entry" link was
 * removed on request and the table has no delete column, so a hospitalisation
 * row cannot be deleted from the UI at all. `remove()` below is deliberately
 * kept — the wire call and its version handling are still correct, so
 * re-attaching a control is a one-liner — but nothing calls it today.
 *
 * Update mode passes `cause.description` through untouched (the form has no
 * field for it, so a re-save must not silently wipe it).
 *
 * expectedVersion is re-read (svc.list) immediately before every mutation —
 * never cached — the same rule the medicine delete established. Sibling sheets
 * are blocked from saving while ours is in flight, so that read cannot go
 * stale between the read and the write.
 */

const MAX_DESC_WORDS = 250;

// Live tally for the label's counter — same words-not-chars rule capWords
// enforces, so the number the user watches is the number the cap acts on.
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};

const capWords = (text) => {
  const words = text.split(/(\s+)/);
  let count = 0;
  let out = "";
  for (const w of words) {
    if (!/\s/.test(w) && w.length > 0) {
      count += 1;
      if (count > MAX_DESC_WORDS) break;
    }
    out += w;
  }
  return count > MAX_DESC_WORDS ? out : text;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");

const shiftDate = (baseISO, y, m, d, sign) => {
  const base = baseISO ? new Date(baseISO) : new Date();
  if (isNaN(base.getTime())) return "";
  const out = new Date(base.getTime());
  out.setFullYear(out.getFullYear() + sign * y);
  out.setMonth(out.getMonth() + sign * m);
  out.setDate(out.getDate() + sign * d);
  return out.toISOString().slice(0, 10);
};

/** Split the gap between two ISO dates into calendar-ish {y, m, d}. */
const splitGap = (fromISO, toISO) => {
  const from = new Date(fromISO);
  const to = new Date(toISO);
  if (isNaN(from.getTime()) || isNaN(to.getTime()) || to < from) return { y: 0, m: 0, d: 0 };
  let y = to.getFullYear() - from.getFullYear();
  let m = to.getMonth() - from.getMonth();
  let d = to.getDate() - from.getDate();
  if (d < 0) {
    m -= 1;
    const prev = new Date(to.getFullYear(), to.getMonth(), 0).getDate();
    d += prev;
  }
  if (m < 0) {
    y -= 1;
    m += 12;
  }
  return { y, m, d };
};

const formatDuration = (fromISO, toISO) => {
  if (!fromISO || !toISO) return "";
  const { y, m, d } = splitGap(fromISO, toISO);
  const parts = [];
  if (y) parts.push(`${y} year${y === 1 ? "" : "s"}`);
  if (m) parts.push(`${m} month${m === 1 ? "" : "s"}`);
  if (d || parts.length === 0) parts.push(`${d} day${d === 1 ? "" : "s"}`);
  return parts.join(" ");
};

const HospitalisationForm = ({
  formId,
  patientId,
  editing: editingProp,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onRecordChange,
  onClose,
  // Transcript-mode extensions (all optional; defaults preserve standalone).
  initialPayload,
  onSave,
  onReject,
  saving,
  error,
  themeClass,
  lockName,
  isReopen,
}) => {
  const p = initialPayload || null;
  const isTranscript = typeof onSave === "function";
  const lockCauseName = !!lockName;

  // The record this sheet writes to. It is STATE, not just the prop, because a
  // formal ICD pick in ADD mode can turn this very sheet into an update of an
  // entry that already exists (the dedup rule in handleCausePick) — the rail
  // hands us `editing` once at mount and never changes it.
  const [editing, setEditing] = useState(() => editingProp || null);
  const [collapsed, setCollapsed] = useState(false);

  // ZY-10356 Phase 1 — transcript-mode draft preservation. No-op in
  // standalone.
  const [draft, saveDraft] = useCardDraft(formId);

  // Seeded in the useState INITIALISERS, never from an effect: the rail mounts
  // one sheet per record and never swaps a mounted sheet's record out, so an
  // effect would only be a second render's worth of churn.
  const seed = editingProp || null;
  const seedCause = (seed && seed.cause) || {};
  const seedHospDate = seed ? toDateInput(seed.hospitalisationDate) : "";
  const seedDischargeDate = seed ? toDateInput(seed.dischargeDate) : "";

  // Transcript-mode seeding: AI's `add_hospitalisation` puts the episode
  // name in `name`, dates in date_of_surgery / date_of_discharge, a
  // single CPT in cpt_code + cpt_description, and an ICD-10-CM code in
  // icd_code + icd_description (when the AI can identify one — the
  // hospitalisation Cause is ICD-backed on the standalone form, same
  // shape as CC / Dx).
  const [causeName, setCauseName] = useState(() => {
    if (draft && "causeName" in draft) return draft.causeName;
    if (seed) return seedCause.icdName || "";
    if (p) return p.icd_description || p.name || p.value || "";
    return "";
  });
  const [causeIcdCode, setCauseIcdCode] = useState(() => {
    if (draft && "causeIcdCode" in draft) return draft.causeIcdCode;
    if (seed) return seedCause.icdCode || "";
    if (p && p.icd_code) return p.icd_code;
    return "";
  });
  // AI-suggested ICD chip payload — frozen at mount from the captured
  // item (transcript-mode only). Passed straight to IcdAutocomplete
  // which owns the pill markup + click behavior; HospitalisationForm
  // just carries the shape through. Null on standalone add / update
  // sheets. Mirrors ComplaintForm / DiagnosisForm.
  const aiSuggestion = p && p.icd_code
    ? { icdCode: p.icd_code, extractedText: p.name || p.value || "" }
    : null;
  // No field on the board — carried invisibly so an update can't wipe it.
  const [causeDescription, setCauseDescription] = useState(() => {
    if (draft && "causeDescription" in draft) return draft.causeDescription;
    if (seed) return seedCause.description || "";
    if (p) return p.name || p.value || "";
    return "";
  });
  // Required (*) — no silent default. Transcript-mode: derive from AI's
  // date_of_discharge presence (matches the Go adapter's hospStatusFromAI).
  const [status, setStatus] = useState(() => {
    if (draft && "status" in draft) return draft.status;
    if (seed) return seed.status || "hospitalised";
    if (p) {
      const disc = p.date_of_discharge || "";
      const s = String(p.status || "").toLowerCase();
      if (disc || s === "completed") return "discharged";
      return "hospitalised";
    }
    return null;
  }); // "discharged" | "hospitalised"
  // Update mode runs the same two-way sync as Add, so the steppers must open
  // already describing the saved dates — "time since admission" and the length
  // of the stay.
  // Transcript-mode date seeding: AI's date_of_surgery → hospDate; date_
  // of_discharge → dischargeDate. Stepper "hospAgo" derives from date-to-
  // today gap so the reviewer sees consistent steppers + date.
  const seedHospDateEff = seedHospDate || ((p && (p.date_of_surgery || p.since_date)) || "");
  const seedDischDateEff = seedDischargeDate || ((p && p.date_of_discharge) || "");
  const [hospAgo, setHospAgo] = useState(() => (
    draft && "hospAgo" in draft
      ? draft.hospAgo
      : (seedHospDateEff ? splitGap(seedHospDateEff, todayISO()) : { y: 0, m: 0, d: 0 })
  ));
  const [hospDate, setHospDate] = useState(() => (
    draft && "hospDate" in draft ? draft.hospDate : seedHospDateEff
  ));
  const [stay, setStay] = useState(() => (
    draft && "stay" in draft
      ? draft.stay
      : (seedHospDateEff && seedDischDateEff ? splitGap(seedHospDateEff, seedDischDateEff) : { y: 0, m: 0, d: 0 })
  ));
  const [dischargeDate, setDischargeDate] = useState(() => (
    draft && "dischargeDate" in draft ? draft.dischargeDate : seedDischDateEff
  ));
  const [procedures, setProcedures] = useState(() => {
    if (draft && "procedures" in draft) return draft.procedures;
    if (seed) return seed.procedures || [];
    if (p && (p.cpt_code || p.cpt_description)) {
      return [{
        code: p.cpt_code || "",
        name: p.cpt_description || p.name || "(untitled)",
        source: p.cpt_code ? "catalog" : "free-text",
      }];
    }
    return [];
  });
  const [description, setDescription] = useState(() => {
    if (draft && "description" in draft) return draft.description;
    if (seed) return seed.description || "";
    if (p) return p.description || "";
    return "";
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change.
  useEffect(() => {
    saveDraft({
      causeName, causeIcdCode, causeDescription, status,
      hospAgo, hospDate, stay, dischargeDate,
      procedures, description,
    });
  }, [
    saveDraft,
    causeName, causeIcdCode, causeDescription, status,
    hospAgo, hospDate, stay, dischargeDate,
    procedures, description,
  ]);

  const isEdit = !!editing;

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // Guards the dedup lookup below against a stale response hijacking the form
  // after the user has moved on to another pick.
  const pickedIcdRef = useRef(seed ? seedCause.icdCode || "" : "");

  // The rail needs to know whether this sheet has a cause on it yet: a second
  // "+ Add hospitalisation" click must not bury an untouched blank sheet under
  // a new one. Update sheets always count as filled — their record came from
  // the table.
  useEffect(() => {
    if (typeof onFilledChange === "function") {
      onFilledChange(formId, isEdit || !!causeName.trim());
    }
  }, [formId, isEdit, causeName, onFilledChange]);

  /** Re-point this sheet at an existing entry and switch it to Update mode. */
  const startEdit = useCallback((h) => {
    if (!h) return;
    const cause = h.cause || {};
    setEditing(h);
    // Tell the RAIL too, not just ourselves. Otherwise its one-sheet-per-record
    // rule still thinks this is a blank add, and clicking this entry's row in
    // the table would open a second sheet for the same record — two updates
    // racing, with the later write silently overwriting the earlier one.
    if (onRecordChange) onRecordChange(formId, h);
    setCauseName(cause.icdName || "");
    setCauseIcdCode(cause.icdCode || "");
    setCauseDescription(cause.description || "");
    pickedIcdRef.current = cause.icdCode || "";
    setStatus(h.status || "hospitalised");
    const hd = toDateInput(h.hospitalisationDate);
    const dd = toDateInput(h.dischargeDate);
    setHospDate(hd);
    setDischargeDate(dd);
    setHospAgo(hd ? splitGap(hd, todayISO()) : { y: 0, m: 0, d: 0 });
    setStay(hd && dd ? splitGap(hd, dd) : { y: 0, m: 0, d: 0 });
    setProcedures(h.procedures || []);
    setDescription(h.description || "");
    setFeedback(null);
  }, [formId, onRecordChange]);

  // Hospitalised clears + freezes the discharge block (backend invariant).
  useEffect(() => {
    if (status === "hospitalised") {
      setStay({ y: 0, m: 0, d: 0 });
      setDischargeDate("");
    }
  }, [status]);

  // ── stepper ↔ date sync (BOTH directions, in Add AND Update) ──
  // Every path below is symmetric: moving a counter recomputes its date, and
  // editing a date back-fills its counters. Update used to render plain date
  // boxes with no counters at all, so neither direction ran there.
  const setHospAgoPart = (part) => (v) => {
    const next = { ...hospAgo, [part]: v };
    setHospAgo(next);
    const today = todayISO();
    const date = shiftDate(today, next.y, next.m, next.d, -1);
    setHospDate(date);
    if (dischargeDate) {
      // Recomputed discharge = admission + current stay. If that lands
      // past today (e.g. admission moved forward while stay stayed the
      // same), cap at today and rewrite the stay steppers so the two
      // stay in sync — otherwise the counters would lie about the gap.
      let proposed = shiftDate(date, stay.y, stay.m, stay.d, +1);
      if (proposed > today) {
        proposed = today;
        setStay(splitGap(date, today));
      }
      setDischargeDate(proposed);
    }
  };
  const editHospDate = (raw) => {
    // Clamp keyboard-typed future dates back to today. The `max` attr on
    // `<input type="date">` caps the native picker but Chrome/Safari still
    // let a user TYPE a date past it — trap the overflow here.
    const today = todayISO();
    const iso = raw && raw > today ? today : raw;
    setHospDate(iso);
    if (!iso) return;
    // Admission date → its own "time since" steppers.
    setHospAgo(splitGap(iso, today));
    if (dischargeDate) {
      // A discharge date is already on screen, so the stay is now knowable:
      // RE-DERIVE it rather than shifting the discharge date. This is the
      // pick-discharge-first order — previously the stay steppers stayed at
      // 00/00/00 because the sync only ran the other way round.
      setStay(splitGap(iso, dischargeDate));
    } else if (stay.y || stay.m || stay.d) {
      // No discharge date yet but a stay was typed — derive the date from it.
      // Cap at today so a large stay + a recent admission can't push the
      // computed discharge into the future.
      let proposed = shiftDate(iso, stay.y, stay.m, stay.d, +1);
      if (proposed > today) {
        proposed = today;
        setStay(splitGap(iso, today));
      }
      setDischargeDate(proposed);
    }
  };
  const setStayPart = (part) => (v) => {
    const next = { ...stay, [part]: v };
    if (hospDate) {
      const today = todayISO();
      let proposed = shiftDate(hospDate, next.y, next.m, next.d, +1);
      if (proposed > today) {
        // Would land in the future — cap the discharge date at today and
        // rewind the stay steppers to the real gap so the counters reflect
        // what actually got persisted.
        setStay(splitGap(hospDate, today));
        setDischargeDate(today);
        return;
      }
      setStay(next);
      setDischargeDate(proposed);
      return;
    }
    setStay(next);
  };
  const editDischargeDate = (raw) => {
    // Same clamp as editHospDate — a keyboard-typed future date snaps back
    // to today so a stay never runs past "now".
    const today = todayISO();
    const iso = raw && raw > today ? today : raw;
    setDischargeDate(iso);
    // The stay is the gap between the two dates, so it can only be derived
    // once BOTH exist. Picking discharge first leaves the steppers at zero
    // until the admission date lands — editHospDate then fills them in.
    if (iso && hospDate) setStay(splitGap(hospDate, iso));
    if (!iso) setStay({ y: 0, m: 0, d: 0 });
  };

  // "Hospitalised" = still admitted: the discharge block is inapplicable.
  const stillAdmitted = status === "hospitalised";

  const durationText = useMemo(() => {
    if (status === "hospitalised") {
      // Still admitted: never "—". Without an admission date all we can say is
      // "Ongoing"; with one we can also say how long it has been running.
      if (!hospDate) return "Ongoing";
      const days = Math.max(0, Math.floor((Date.now() - new Date(hospDate).getTime()) / 86400000));
      return `${days} day${days === 1 ? "" : "s"} · ongoing`;
    }
    if (!hospDate || !dischargeDate) return "—";
    return formatDuration(hospDate, dischargeDate) || "—";
  }, [status, hospDate, dischargeDate]);

  // Dedup key: CPT code when present, otherwise the lowercased name (kept as
  // a fallback so legacy free-text rows loaded on Edit still round-trip).
  const procKey = (p) =>
    ((p && p.code) || (p && p.name) || "").toString().trim().toLowerCase();

  const addProcedure = useCallback((p) => {
    setProcedures((prev) => {
      const key = procKey(p);
      if (!key) return prev;
      if (prev.some((x) => procKey(x) === key)) return prev;
      return [...prev, p];
    });
  }, []);
  const removeProcedure = (target) =>
    setProcedures((prev) => prev.filter((p) => procKey(p) !== procKey(target)));

  // Set fed to the picker so already-added rows render greyed and unclickable.
  const pickedProcSet = useMemo(
    () => new Set(procedures.map(procKey).filter(Boolean)),
    [procedures]
  );

  const handleCausePick = ({ complaint, complaintCode }) => {
    const icd = (complaintCode && complaintCode.icdCode) || "";
    setCauseName(complaint || "");
    setCauseIcdCode(icd);
    pickedIcdRef.current = icd;
    if (isEdit) {
      // The carried cause note belongs to the record's OWN diagnosis. Landing
      // back on that diagnosis restores the note; moving to a different one
      // drops it, rather than shipping it under a cause that means something
      // else. (Codeless legacy causes are matched on the name instead.)
      const stored = editing.cause || {};
      const sameCause = stored.icdCode
        ? !!icd && icd === stored.icdCode
        : (complaint || "").trim().toLowerCase() ===
          (stored.icdName || "").trim().toLowerCase();
      setCauseDescription(sameCause ? stored.description || "" : "");
      return;
    }
    // Upstream dedup rule: in ADD, a formal pick that matches an existing
    // entry switches THIS sheet to EDITING that entry instead of duplicating.
    if (!icd) return;
    const svc = new HospitalisationService();
    svc.list(patientId, (fresh) => {
      if (!mounted.current || pickedIcdRef.current !== icd) return;
      const match = ((fresh && fresh.hospitalisations) || []).find(
        (x) => x.cause && x.cause.icdCode === icd
      );
      if (match) startEdit(match);
    });
  };

  const isBusy = submitting || !!saving;
  // Save gate. Cause of hospitalisation must be an ICD-backed pick from
  // the dropdown — free-text (name without a code) is rejected so every
  // saved row lands with a real ICD. On the transcript flow, the
  // IcdAutocomplete auto-picks an exact match on mount when the AI
  // populated a plain name; if no exact hit, the reviewer has to open
  // the dropdown and pick before Save enables.
  const causeHasIcd = !!(causeIcdCode || "").toString().trim();
  const canSubmit =
    !blockedBySibling &&
    !isBusy &&
    !!causeName.trim() &&
    causeHasIcd &&
    status !== null &&
    !!hospDate &&
    (status === "hospitalised" || (!!dischargeDate && dischargeDate >= hospDate));

  const failed = (err) => {
    // Unmounted means the rail already dropped this sheet, and closeSheet
    // clears the saving flag on the way out — so there is nothing to hand back
    // and no parent state to touch.
    if (!mounted.current) return;
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, false);
    }
    setSubmitting(false);
    setFeedback({ kind: "err", text: (err && err.message) || "Save failed — please retry" });
  };

  const submit = () => {
    if (!canSubmit) return;
    if (!isTranscript && !patientId) return;
    const body = {
      cause: {
        icdCode: causeIcdCode,
        icdName: causeName.trim(),
        description: (causeDescription || "").trim(),
      },
      status,
      hospitalisationDate: toIsoUtc(hospDate),
      dischargeDate: status === "discharged" && dischargeDate ? toIsoUtc(dischargeDate) : undefined,
      procedures,
      description: description.trim(),
    };

    // Transcript mode — hand out; caller owns persistence. Skip local
    // submitting/feedback + HOSP_V2_REFRESH emit.
    if (isTranscript) {
      onSave(body);
      return;
    }

    setSubmitting(true);
    if (typeof onSavingChange === "function") {
      onSavingChange(formId, true);
    }
    setFeedback(null);

    const svc = new HospitalisationService();
    const done = () => {
      EventService.emit(HOSP_V2_REFRESH);
      if (!mounted.current) return;
      if (typeof onSavingChange === "function") {
        onSavingChange(formId, false);
      }
      setSubmitting(false);
      // The rail drops this card, which unmounts us — so nothing is reset or
      // re-rendered here. Anything set after this point would be a setState on
      // an unmounted component.
      onClose(formId);
    };
    // Fresh version per mutation, never cached.
    svc.list(
      patientId,
      (fresh) => {
        const withVersion = { ...body, expectedVersion: (fresh && fresh.version) || 0 };
        if (editing) {
          svc.update(patientId, editing.hospitalisationId, withVersion, done, failed);
        } else {
          svc.add(patientId, withVersion, done, failed);
        }
      },
      failed
    );
  };

  // Retained but unreferenced — see the file header. Re-attach a control to
  // restore delete; the wire call and version handling below are intact.
  // eslint-disable-next-line no-unused-vars
  const remove = () => {
    // blockedBySibling too — see the lab rail: a DELETE inside a sibling's
    // list->write window breaks the serialisation invariant.
    if (!editing || submitting || blockedBySibling || !patientId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${causeName.trim() || "this entry"} from hospitalisation history?`)) {
      return;
    }
    setSubmitting(true);
    onSavingChange(formId, true);
    setFeedback(null);
    const svc = new HospitalisationService();
    const deleteFailed = (err) => {
      // A version clash means the table is showing a stale doc — pull it fresh
      // so the user isn't looking at a row that no longer exists.
      if (err && err.reason === "version") EventService.emit(HOSP_V2_REFRESH);
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      setFeedback({ kind: "err", text: (err && err.message) || "Delete failed — please retry" });
    };
    svc.list(
      patientId,
      (fresh) => {
        svc.delete(
          patientId,
          editing.hospitalisationId,
          (fresh && fresh.version) || 0,
          () => {
            EventService.emit(HOSP_V2_REFRESH);
            if (!mounted.current) return;
            onSavingChange(formId, false);
            setSubmitting(false);
            onClose(formId);
          },
          deleteFailed
        );
      },
      deleteFailed
    );
  };

  // Unique per sheet — stacked cards would otherwise share DOM ids and every
  // label would point at the first card's field.
  const hospDateId = `hs3-hosp-date-${formId}`;
  const dischargeDateId = `hs3-discharge-date-${formId}`;
  const descId = `hs3-desc-${formId}`;

  // The badge rides INSIDE the title/label element, not as a sibling: the
  // .cc3-lbl-row it sits in is a space-between flex row, which would fling a
  // loose badge to the far edge.
  const badge = showOrdinal ? (
    <span className="zy-ord" aria-hidden="true">{ordinal}</span>
  ) : null;

  const foldId = `hs3-fold-${formId}`;

  const rootClass = [
    "cc3-form",
    "hs3-form",
    collapsed ? "is-collapsed" : "",
    themeClass || "",
  ].filter(Boolean).join(" ");
  const displayError = feedback && feedback.kind === "err"
    ? feedback.text
    : error || "";
  const displayErrorKind = displayError ? "err" : "";

  return (
    <div className={rootClass}>
      {/* ZY-10356 Phase 6 Rule 1 — top-right X close only on re-opens
          (#2/#4/#6/#8). First-opens (#1/#3/#5/#7) leave the footer
          Reject as the sole dismiss affordance; gate matches CC/Med. */}
      {isTranscript && isReopen ? (
        <button
          type="button"
          className="zy-card-x"
          onClick={() => onClose(formId)}
          aria-label="Close"
        >
          &times;
        </button>
      ) : null}
      {/* The chevron-up folds everything below this row, footer included. */}
      <div className="cc3-lbl-row">
        {isEdit ? (
          <span className="hs3-update-title">{badge}Update hospitalisation</span>
        ) : lockCauseName ? (
          <span className="hs3-update-title">
            {badge}Hospitalisation
            <span className="cc3-update-name">&middot; {causeName || "(none)"}</span>
          </span>
        ) : (
          <label className="cc3-lbl">
            {badge}
            Hospitalisation{" "}
            <span className="cc3-lbl-hint">&middot; ICD-enabled &middot; fuzzy search</span>
          </label>
        )}
        <button
          type="button"
          className="cc3-lbl-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={
            collapsed ? "Expand this hospitalisation" : "Collapse this hospitalisation"
          }
        >
          <ChevronUpIcon />
        </button>
      </div>

      <div className="cc3-fold is-tight" id={foldId}>
        <div className="cc3-fold-inner">
      <div className="cc3-field">
        <label className="hs3-sublbl">
          Cause of hospitalisation (Diagnosis)<span className="cc3-req">*</span>
        </label>
        {isEdit || lockCauseName ? (
          /* Locked identity — the same rule Allergies and Physical activity
             already apply in Update mode. The cause is what the record IS;
             re-pointing it at a different diagnosis would silently rewrite
             history rather than update this stay, and the audit trail has no
             way to express that. Everything else on the sheet stays editable;
             a genuinely different cause is a different hospitalisation.
             Transcript-mode re-edit also passes lockName=true (review
             click-to-edit) so the reviewer can only tweak dates / status
             / procedures / description from there. */
          <div className="alg-locked" aria-readonly="true">
            <span className="alg-locked-name">{causeName || "—"}</span>
            {causeIcdCode && (
              <span className="alg-locked-tag">ICD {causeIcdCode}</span>
            )}
          </div>
        ) : (
          <IcdAutocomplete
            value={causeName}
            code={causeIcdCode}
            onChange={handleCausePick}
            disabled={isBusy}
            placeholder="Search a diagnosis"
            aiSuggestion={aiSuggestion}
          />
        )}
      </div>

      <div className="cc3-field">
        <label className="cc3-lbl">
          Status<span className="cc3-req">*</span>
        </label>
        {/* aria-label IS this radiogroup's accessible name (these are buttons,
            not inputs — there is no name= attribute to namespace). The sheet's
            own role="group" label disambiguates the stack. */}
        <div className="yn-toggle" role="radiogroup" aria-label="Status">
          {/* Board order: Discharged first (3073:7084). */}
          {[
            ["discharged", "Discharged"],
            ["hospitalised", "Hospitalised"],
          ].map(([v, lbl]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={status === v}
              className={`yn-opt${status === v ? " is-on" : ""}`}
              onClick={() => setStatus(v)}
              disabled={isBusy}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="cc3-field">
        <label className="cc3-lbl" htmlFor={hospDateId}>
          Date of hospitalisation<span className="cc3-req">*</span>
        </label>
        <div className="cc-duration" role="group" aria-label="Duration">
          <Stepper label="Years" value={hospAgo.y} onChange={setHospAgoPart("y")} max={120} disabled={isBusy} />
          <Stepper label="Months" value={hospAgo.m} onChange={setHospAgoPart("m")} max={11} disabled={isBusy} />
          <Stepper label="Days" value={hospAgo.d} onChange={setHospAgoPart("d")} max={31} disabled={isBusy} />
        </div>
        <div className="an3-date-wrap">
          <input
            id={hospDateId}
            className="sec-input"
            type="date"
            value={hospDate}
            onChange={(e) => editHospDate(e.target.value)}
            max={todayISO()}
            disabled={isBusy}
            aria-label="Date of hospitalisation"
          />
          <span className="an3-date-icon" aria-hidden="true"><CalendarIcon /></span>
        </div>
      </div>

      {/* Still admitted ⇒ there is no discharge date to give. The whole
          block greys out and stops accepting input; the duration below
          reads as ongoing. */}
      <div className={`cc3-field${stillAdmitted ? " is-locked" : ""}`}>
        <label className="cc3-lbl" htmlFor={dischargeDateId}>
          Date of discharge
          {!stillAdmitted && <span className="cc3-req">*</span>}
        </label>
        <div className="cc-duration" role="group" aria-label="Duration">
          <Stepper label="Years" value={stay.y} onChange={setStayPart("y")} max={120} disabled={stillAdmitted || submitting} />
          <Stepper label="Months" value={stay.m} onChange={setStayPart("m")} max={11} disabled={stillAdmitted || submitting} />
          <Stepper label="Days" value={stay.d} onChange={setStayPart("d")} max={31} disabled={stillAdmitted || submitting} />
        </div>
        <div className="an3-date-wrap">
          <input
            id={dischargeDateId}
            className="sec-input"
            type="date"
            value={dischargeDate}
            onChange={(e) => editDischargeDate(e.target.value)}
            max={todayISO()}
            min={hospDate || undefined}
            disabled={submitting || stillAdmitted}
            aria-label="Date of discharge"
          />
          <span className="an3-date-icon" aria-hidden="true"><CalendarIcon /></span>
        </div>
        <p className="cc3-autocalc">
          Auto-calc &middot; Duration: <strong>{durationText}</strong>
        </p>
      </div>

      <div className="cc3-field">
        <label className="cc3-lbl">
          Procedures during hospitalisation{" "}
          <span className="cc3-lbl-hint">&middot; add multiple</span>
        </label>
        <ProcedureCatalogAutocomplete
          onAdd={addProcedure}
          disabled={isBusy}
          placeholder="Search procedure — CPT code"
          pickedSet={pickedProcSet}
        />
        {procedures.length > 0 && (
          <div className="hs3-proc-chips">
            {procedures.map((p) => (
              <span className="hs3-proc-chip" key={procKey(p)} title={p.name}>
                {/* Name in its OWN flex-1 span so long labels truncate
                    inside the chip without pushing the CPT pill and the
                    remove button off-screen. */}
                <span className="hs3-proc-name">{p.name}</span>
                {p.code ? <span className="hs3-proc-code">{p.code}</span> : null}
                <button
                  type="button"
                  className="hs3-proc-x"
                  onClick={() => removeProcedure(p)}
                  aria-label={`Remove ${p.name}`}
                >
                  &#10005;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="cc3-field">
        <div className="cc3-lbl-row">
          <label className="cc3-lbl" htmlFor={descId}>
            Description <span className="cc3-lbl-opt">optional</span>
          </label>
          {/* Family-history parity: a LIVE `n / 250 words` tally once anything
              is typed, the static ceiling while the field is empty. */}
          <span className="cc3-lbl-max">
            {description
              ? `${countWords(description)} / ${MAX_DESC_WORDS} words`
              : `Max ${MAX_DESC_WORDS} words`}
          </span>
        </div>
        <textarea
          id={descId}
          className="cc3-textarea"
          rows={3}
          value={description}
          onChange={(e) => setDescription(capWords(e.target.value))}
          placeholder="Add a short note about this stay…"
          disabled={isBusy}
        />
      </div>

      {displayError && <div className={`cc-feedback is-${displayErrorKind}`}>{displayError}</div>}

      <div className="cc3-foot">
        {/* Stays live while saving ON PURPOSE. A write that never settles (the
            token-refresh path can swallow its callbacks) would otherwise pin
            the stack-wide save lock with no way out — every sheet's Save dead
            and this sheet undismissable until a reload. Closing mid-write is
            safe: the rail clears the lock, and both done() and failed() bail on
            !mounted, so a late response touches nothing. */}
        <button
          type="button"
          className="cc3-cancel"
          onClick={() => {
            if (isTranscript && !isBusy && typeof onReject === "function") {
              onReject();
              return;
            }
            onClose(formId);
          }}
        >
          {isBusy ? "Close" : (isTranscript ? "Reject" : "Cancel")}
        </button>
        <button
          type="button"
          className={`cc3-save${themeClass ? ` ${themeClass}` : ""}`}
          onClick={submit}
          disabled={!canSubmit}
          title={blockedBySibling ? "Another hospitalisation is saving" : undefined}
        >
          {isBusy
            ? (isEdit ? "Updating…" : (isTranscript ? "Adding…" : "Saving…"))
            : blockedBySibling
            ? "Waiting…"
            : isEdit
            ? "Update"
            : isTranscript
            ? "Add"
            : "Save hospitalisation"}
        </button>
      </div>
        </div>
      </div>
    </div>
  );
};

HospitalisationForm.propTypes = {
  /** Stable per-sheet id; namespaces DOM ids and keys the rail's bookkeeping. */
  formId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  patientId: PropTypes.number,
  /** null = blank add; a hospitalisation record = updating that entry. */
  editing: PropTypes.object,
  /** 1-based position in the stack, top first. */
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  /** A sibling sheet is mid-save; ours must wait so the version read is safe. */
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func,
  onSavingChange: PropTypes.func,
  /** Called when a formal ICD pick re-points this sheet at an existing entry. */
  onRecordChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  // Transcript-mode extensions — all optional; unset preserves standalone.
  initialPayload: PropTypes.object,
  onSave: PropTypes.func,
  onReject: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
  themeClass: PropTypes.string,
  /**
   * Lock the cause (name + ICD) as read-only. Add-mode transcript
   * callers set true when reopening an already-saved captured item
   * (review click-to-edit) so the reviewer can only tweak dates /
   * status / procedures / description from there.
   */
  lockName: PropTypes.bool,
  /** Transcript-mode: true when re-opening a staged item. Gates the X top-right. */
  isReopen: PropTypes.bool,
};

HospitalisationForm.defaultProps = {
  initialPayload: null,
  onSave: null,
  saving: false,
  error: "",
  themeClass: "",
  lockName: false,
  isReopen: false,
};

export default HospitalisationForm;
